import { useMemo } from 'react';

import { CareServiceOrgansKey, REPORT_FINDING_STATUS, ReportFinding } from '@/api';
import { useFctReport, useFctReportFindingsByReport, useReportOrgansMap } from '@/shared/hook';
import { ReportLink } from '@/shared/model';

import { LESION_FINDINGS } from '../constant';

/**
 * Hook to detect draft AI-generated lesion findings across multiple organs.
 *
 * @param {ReportLink[]} links - The organ links to check for draft lesion findings.
 * @returns {Record<string, boolean>} - Object mapping organ keys to their draft lesion status.
 *
 * Excludes organs with incomplete scans from validation.
 * Used for report validation to ensure all lesions across all organs are published before submission.
 */
export const useOrgansWithDraftReportFindingLesions = (
  links: ReportLink[],
): Record<string, boolean> => {
  const { reportFindings } = useFctReportFindingsByReport();
  const { report } = useFctReport();
  const reportOrgansMap = useReportOrgansMap();

  return useMemo(() => {
    if (!links || !report?.uuid) {
      return {};
    }

    return links.reduce(
      (acc, link) => {
        const organKey = link.key as CareServiceOrgansKey;
        const reportOrgan = reportOrgansMap?.[organKey];
        const hasIncompleteScan = reportOrgan?.incompleteScan ?? false;

        if (hasIncompleteScan) {
          return { ...acc, [link.key]: false };
        }

        const organFindings = reportFindings.filter(
          (finding: ReportFinding) => finding.template?.organ === link.key,
        );

        const hasDraftLesion = organFindings.some(
          (finding: ReportFinding) =>
            LESION_FINDINGS.includes(finding.template?.condition as string) &&
            finding.status === REPORT_FINDING_STATUS.DRAFT,
        );

        return { ...acc, [link.key]: hasDraftLesion };
      },
      {} as Record<string, boolean>,
    );
  }, [links, report?.uuid, reportFindings, reportOrgansMap]);
};
